import { useState, useRef, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  CLIENT_KEY_STORAGE,
  MENTEE_ENTRY_GATE_STORAGE,
  MENTEE_KEY_STORAGE,
  CLASSROOM_PORTAL_ROUTES,
  REGISTRATION_PATHWAY,
  REGISTRATION_PATHWAY_STORAGE,
  SCREENS,
  isClassroomPortalRoute,
  isPortalEntranceKey,
  isTempOrgKey,
  normalizeClientKey,
} from '../lib/screenRegistry';
import { ROUTES } from '../lib/classroomRoutes';
import { getMasterTestingKey, isMasterTestingKey } from '../lib/masterTestingKey';
import { useMaintenancePreviewMount } from '../lib/maintenancePreviewMountContext';
import './PortalLogin.css';

/** Screen-MPL-06 — Classroom Portal Access Gateway (`/classroom-elt`). */
export default function PortalLogin() {
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const submittedRef = useRef(false);
  const previewMount = useMaintenancePreviewMount();
  const isPreviewEmbed = previewMount.active;

  const portalKeyValid = useMemo(
    () => isMasterTestingKey(key) || isPortalEntranceKey(key),
    [key],
  );

  if (!isClassroomPortalRoute(location.pathname) && !isPreviewEmbed) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || submittedRef.current) return;

    if (isPreviewEmbed) {
      setError('Maintenance preview — key validation is display-only in this frame.');
      return;
    }

    const registrationKey = normalizeClientKey(key.trim());
    if (!registrationKey) {
      setError('Please enter your registration key.');
      return;
    }

    if (isMasterTestingKey(key.trim())) {
      submittedRef.current = true;
      setLoading(true);
      setError('');
      try {
        await login(key.trim());
        sessionStorage.setItem(MENTEE_KEY_STORAGE, getMasterTestingKey());
        sessionStorage.setItem(MENTEE_ENTRY_GATE_STORAGE, 'portal');
        navigate(ROUTES.EXECUTIVE_INTRO, { replace: true });
      } catch (err: unknown) {
        submittedRef.current = false;
        setError((err as { error?: string }).error || 'Authentication failed');
        setLoading(false);
      }
      return;
    }

    if (!isPortalEntranceKey(registrationKey)) {
      setError('Enter a valid key: MPL-XXXX, MPL-XXX-XXXX, MLPT-XXXX, or MPLO-XXXX.');
      return;
    }

    submittedRef.current = true;
    setLoading(true);
    setError('');

    try {
      await login(registrationKey);

      if (isTempOrgKey(registrationKey)) {
        sessionStorage.setItem(CLIENT_KEY_STORAGE, registrationKey);
        sessionStorage.setItem(REGISTRATION_PATHWAY_STORAGE, REGISTRATION_PATHWAY.PROCUREMENT);
      } else {
        sessionStorage.setItem(MENTEE_KEY_STORAGE, registrationKey);
      }

      sessionStorage.setItem(MENTEE_ENTRY_GATE_STORAGE, 'portal');
      navigate(ROUTES.EXECUTIVE_INTRO, { replace: true });
    } catch (err: unknown) {
      submittedRef.current = false;
      setError((err as { error?: string }).error || 'Authentication failed');
      setLoading(false);
    }
  };

  const handleKeyChange = (value: string) => {
    setKey(value);
    if (error) setError('');
    submittedRef.current = false;
  };

  return (
    <div
      className="portal-login-screen"
      data-screen-id={SCREENS.MPL06}
      data-screen-surface="classroom-portal"
      data-screen-route={CLASSROOM_PORTAL_ROUTES.PORTAL}
    >
      <div className="portal-login-logo-wrap">
        <img
          src={encodeURI('/Assets/logo/mentology- logo.png')}
          alt="Mentology"
          className="portal-login-logo brand-logo"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </div>
      <div className="portal-login-content">
        <h1 className="section-header portal-login-heading">Classroom Portal</h1>
        <p className="portal-login-instruction">Please enter your registration key.</p>
        <form onSubmit={handleSubmit} className="portal-login-form" noValidate>
          <input
            className="input-field portal-login-input"
            type="text"
            name="registrationKey"
            autoComplete="off"
            spellCheck={false}
            placeholder="MPL-XXXX / MPL-XXX-XXXX / MLPT-XXXX / MPLO-XXXX"
            value={key}
            onChange={(e) => handleKeyChange(e.target.value)}
            aria-label="Registration key"
          />
          {error && <p className="error-text portal-login-error">{error}</p>}
          <div className="button-control-group portal-login-actions">
            <button type="submit" className="btn-primary" disabled={loading || !portalKeyValid}>
              {loading ? 'Validating...' : 'VALIDATE KEY'}
            </button>
            <Link to={ROUTES.LANDING} className="btn-primary">Back to Home</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
